import { getBaseUrl, getAuthHeaders } from './auth';

const api = async (path, options = {}, _retried = false) => {
  const url = `${getBaseUrl()}${path}`;
  const defaultHeaders = getAuthHeaders();
  const res = await fetch(url, {
    ...options,
    headers: {
      ...defaultHeaders,
      ...(options.headers || {}),
    },
  });
  if (res.status === 429) {
    const retryAfter = res.headers.get('Retry-After') || '20';
    // Self-heal a transient page-load burst: wait the server's Retry-After
    // (capped so the UI never hangs) and retry ONCE before surfacing an error.
    if (!_retried) {
      const waitMs = Math.min(parseInt(retryAfter, 10) || 5, 6) * 1000;
      await new Promise((r) => setTimeout(r, waitMs));
      return api(path, options, true);
    }
    const lang = localStorage.getItem('sorane_lang') || 'en';
    const msg = lang === 'zh'
      ? `请求过于频繁，请登录以获得更高访问频率，或等待 ${retryAfter} 秒后重试。`
      : `Rate limit exceeded. Please login for higher rate limits, or wait ${retryAfter}s.`;
    throw new Error(msg);
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    // Expired/invalid JWT: clear the stale token so isLoggedIn() is false and
    // the user is cleanly sent to login instead of hitting a broken page.
    if (res.status === 401 && localStorage.getItem('jwt_token')) {
      const msg = (err.error || '').toLowerCase();
      if (msg.includes('expired') || msg.includes('invalid token') || msg.includes('login required')) {
        localStorage.removeItem('jwt_token');
        if (!window.location.pathname.startsWith('/login')) {
          window.location.assign('/login');
        }
      }
    }
    throw new Error(err.error || res.statusText);
  }
  return res.json();
};

export const login = (email, password, trusted = false) =>
  api('/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, trusted }),
  });

export const register = (name, email, password) =>
  api('/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, email, password }),
  });

export const verifyEmail = (email, code) =>
  api('/verify_email', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });

export const evaluateStocks = (tickerList) =>
  api('/evaluate_stocks', {
    method: 'POST',
    body: JSON.stringify({ ticker_list: tickerList }),
  });

export const getStockNews = (ticker) =>
  api('/get_stock_news', {
    method: 'POST',
    body: JSON.stringify({ ticker }),
  });

export const getStockHistoricalData = (ticker, days = 90) =>
  api('/get_stock_historical_data', {
    method: 'POST',
    body: JSON.stringify({ ticker, days }),
  });

export const getFinancialReport = (ticker, years = 3) =>
  api('/get_stock_financial_report', {
    method: 'POST',
    body: JSON.stringify({ ticker, years }),
  });

export const getStockRecommendations = (sectors = null, topN = 20, topPerSector = 5) =>
  api('/get_stock_recommendations', {
    method: 'POST',
    body: JSON.stringify({ sectors, top_n: topN, top_per_sector: topPerSector }),
  });

export const getCnStockRecommendations = (indices = null, topN = 20, topPerSector = 5) =>
  api('/get_cn_stock_recommendations', {
    method: 'POST',
    body: JSON.stringify({ indices, top_n: topN, top_per_sector: topPerSector }),
  });

export const getMarketEmotion = (market = 'US') =>
  api(`/market_emotion?market=${market}`);

// ── Version ──
export const getVersion = () => api('/version');

// ── Day Trader ──
export const getDayTraderStatus = (ticker = 'MU') =>
  api(`/daytrader/status?ticker=${encodeURIComponent(ticker)}`);

export const getDayTraderSignals = (ticker = 'MU', days = 7, limit = 200) =>
  api(`/daytrader/signals?ticker=${encodeURIComponent(ticker)}&days=${days}&limit=${limit}`);

export const getDayTraderChart = (ticker = 'MU') =>
  api(`/daytrader/chart?ticker=${encodeURIComponent(ticker)}`);

export const getDayTraderTickers = () => api('/daytrader/tickers');

export const getDayTraderMx1Status = () => api('/daytrader/mx1_status');

export const getDayTraderMx1Orders = (days = 7) =>
  api(`/daytrader/mx1_orders?days=${days}`);

export const getDayTraderMx1Config = () => api('/daytrader/mx1_config');

export const updateDayTraderMx1Config = (patch) =>
  api('/daytrader/mx1_config', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });

export const getDayTraderLongTerm = (refresh = false) =>
  api(`/daytrader/long_term${refresh ? '?refresh=1' : ''}`);

// px1 post-market bot (engine b) — status + money (budget/enabled) config
export const getDayTraderPx1Status = () => api('/daytrader/px1_status');

export const getDayTraderPx1Config = () => api('/daytrader/px1_config');

export const updateDayTraderPx1Config = (patch) =>
  api('/daytrader/px1_config', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });

export const getDayTraderPositions = (refresh = false) =>
  api(`/daytrader/positions${refresh ? '?refresh=1' : ''}`);

export const getDayTraderConfig = () => api('/daytrader/config');

export const updateDayTraderConfig = (patch) =>
  api('/daytrader/config', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });

// ── CN Day Trader (signal + email only; tushare codes e.g. 600519.SH) ──
export const getCnDayTraderStatus = (ticker = '600519.SH') =>
  api(`/cn_daytrader/status?ticker=${encodeURIComponent(ticker)}`);

export const getCnDayTraderSignals = (ticker = '600519.SH', days = 7, limit = 200) =>
  api(`/cn_daytrader/signals?ticker=${encodeURIComponent(ticker)}&days=${days}&limit=${limit}`);

export const getCnDayTraderTickers = () => api('/cn_daytrader/tickers');

export const getCnDayTraderConfig = () => api('/cn_daytrader/config');

export const updateCnDayTraderConfig = (patch) =>
  api('/cn_daytrader/config', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
